import * as React from "react";
import { httpsCallable } from "firebase/functions";
import { v4 as uuidv4 } from "uuid";
import { useToasts } from "react-toast-notifications";
import { IClient } from "intake_shared/src/types";
import { fxs } from "../../../../../utils/firestore";
import { Dialog } from "../../../bits/Dialog/Dialog";
import { Input } from "../../../bits/Input/Input";
import {
    DEFAULT_TEST_RECIPIENT,
    E164_PHONE,
    LeaHandoffResult,
    handoffErrorMessage,
    handoffResultToast,
} from "./leaHandoff";

interface LeaHandoffDialogProps {
    client: IClient;
    onClose: () => void;
    /** Called after the backend accepts the handoff. */
    onHandedOff?: () => void;
}

/** Asks for the number Lea AI will text, then hands the inquiry to Lea AI. */
export default function LeaHandoffDialog({
    client,
    onClose,
    onHandedOff,
}: LeaHandoffDialogProps) {
    const { addToast } = useToasts();
    const [recipient, setRecipient] = React.useState(DEFAULT_TEST_RECIPIENT);
    const [pending, setPending] = React.useState(false);

    const handOff = async () => {
        if (pending) return;
        setPending(true);
        try {
            const call = httpsCallable<
                {
                    client_id: string;
                    event_id: string;
                    action: string;
                    test_recipient?: string;
                },
                LeaHandoffResult
            >(fxs, "httpIntakeEngagementDraft", { timeout: 120_000 });
            const result = await call({
                client_id: client.id,
                event_id: uuidv4(),
                action: "handoff",
                test_recipient: recipient.trim(),
            });
            const toast = handoffResultToast(result.data);
            addToast(toast.text, { appearance: toast.appearance });
            setPending(false);
            onHandedOff?.();
            onClose();
        } catch (error) {
            addToast(handoffErrorMessage(error), { appearance: "error" });
            setPending(false);
        }
    };

    // Keep the dialog open while the handoff runs so it cannot be reopened and
    // submitted again before the first request settles.
    const handleClose = () => {
        if (!pending) onClose();
    };

    return (
        <Dialog
            open
            title="Hand to Lea AI"
            onClose={handleClose}
            onSave={handOff}
            saveText={pending ? "Handing off…" : "Hand to Lea AI"}
            isSubmitBtnDisabled={pending || !E164_PHONE.test(recipient.trim())}
            isCancelBtnDisabled={pending}
        >
            <Input
                name="lea-test-recipient"
                label="Number Lea AI will text"
                type="tel"
                value={recipient}
                onChange={(event) => setRecipient(event.target.value)}
                placeholder="+13807103456"
                isRequired
            />
            <p>
                If the number on file is not the best one, enter a different number
                here. Lea AI will use it to contact the client. The number on file
                will not change.
            </p>
        </Dialog>
    );
}
